import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Subject } from 'rxjs';
import { environment } from '../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class ThemeService {
 private storageKey = 'user-theme';
 private apiUrl = environment.apiUrl;

 /** Emits whenever the theme changes so chart widgets can re-colour live. */
 readonly themeChanged$ = new Subject<void>();

constructor(private http: HttpClient) {
    this.loadTheme();
    this.restoreAppearance();
  }

  /* ════════════════════════════════════════════════════════
     APPEARANCE PRESETS  (slide-in theme switcher)
     A preset is just a map of CSS custom properties applied on
     <html>. Inline props on the root element beat styles.css :root,
     so the whole app re-themes instantly. Choice persists in
     localStorage and is re-applied on every load.
  ════════════════════════════════════════════════════════ */
  private appearanceKey = 'erp-appearance';

  /** Apply a full theme preset (set of CSS variables). */
  applyPreset(vars: Record<string, string>, presetId: string, persist = true): void {
    const root = document.documentElement;
    Object.entries(vars).forEach(([k, v]) => root.style.setProperty(k, v));
    if (vars['--brand']) root.style.setProperty('--mdc-theme-primary', vars['--brand']);
    // Marker so theme-specific (e.g. dark) global overrides can be scoped.
    root.setAttribute('data-appearance', presetId || 'sky');
    if (persist) {
      const s = this.getSavedAppearance();
      s.presetId = presetId;
      s.vars = vars;
      delete s.accent;            // a fresh preset owns its own brand colour
      this.saveAppearance(s);
    }
    this.themeChanged$.next();
  }

  /** Override just the accent (brand) colour on top of the current preset. */
  applyAccent(accent: string, persist = true): void {
    const root = document.documentElement;
    root.style.setProperty('--brand', accent);
    root.style.setProperty('--brand-dark', this.shade(accent, -18));
    root.style.setProperty('--brand-light', this.shade(accent, 22));
    root.style.setProperty('--brand-gradient',
      `linear-gradient(135deg, ${this.shade(accent, 22)} 0%, ${accent} 55%, ${this.shade(accent, -18)} 100%)`);
    root.style.setProperty('--mdc-theme-primary', accent);
    if (persist) { const s = this.getSavedAppearance(); s.accent = accent; this.saveAppearance(s); }
    this.themeChanged$.next();
  }

  /** Current theme's chart palette, read from the CSS variables. Widgets call
      this for graph colours and re-call it on themeChanged$. */
  getChartColors(): { series: string[]; grid: string; axis: string } {
    const cs = getComputedStyle(document.documentElement);
    const v = (name: string, fallback: string) => (cs.getPropertyValue(name).trim() || fallback);
    return {
      series: [
        v('--chart-1', '#2f6bed'), v('--chart-2', '#10b981'), v('--chart-3', '#f59e0b'),
        v('--chart-4', '#8b5cf6'), v('--chart-5', '#ef4444'),
      ],
      grid: v('--chart-grid', '#dbe7f8'),
      axis: v('--chart-axis', '#5b6b86'),
    };
  }

  /** Scale the root font size (medium = browser default). */
  applyFontScale(scale: 'small' | 'medium' | 'large', persist = true): void {
    const px = scale === 'small' ? '14px' : scale === 'large' ? '18px' : '16px';
    document.documentElement.style.fontSize = px;
    if (persist) { const s = this.getSavedAppearance(); s.font = scale; this.saveAppearance(s); }
  }

  /** Re-apply the saved appearance on app start (from localStorage cache). */
  restoreAppearance(): void {
    const s = this.getSavedAppearance();
    if (s.vars) this.applyPreset(s.vars, s.presetId, false);
    if (s.accent) this.applyAccent(s.accent, false);
    if (s.font) this.applyFontScale(s.font, false);
  }

  /** Save the current appearance against the logged-in user in the DB so the
      theme follows them to any device / next login. Fire-and-forget. */
  persistToServer(): void {
    const appearance = this.getSavedAppearance();
    this.http.post(`${this.apiUrl}/Users/theme`, { theme: JSON.stringify(appearance) })
      .subscribe({ next: () => {}, error: () => {} });
  }

  /** Fetch this user's saved appearance from the DB and apply it (also caches
      it to localStorage). Call right after login / when the shell loads. */
  loadThemeFromServer(): void {
    this.http.get<{ theme: string }>(`${this.apiUrl}/Users/theme`).subscribe({
      next: (res) => {
        if (!res || !res.theme) return;
        let s: any;
        try { s = JSON.parse(res.theme); } catch { return; }
        if (s.vars) this.applyPreset(s.vars, s.presetId, true);
        if (s.accent) this.applyAccent(s.accent, true);
        if (s.font) this.applyFontScale(s.font, true);
      },
      error: () => {}
    });
  }

  getSavedAppearance(): any {
    return JSON.parse(localStorage.getItem(this.appearanceKey) || '{}');
  }

  private saveAppearance(s: any): void {
    localStorage.setItem(this.appearanceKey, JSON.stringify(s));
  }

  /** Lighten (+%) or darken (-%) a hex colour. */
  shade(hex: string, percent: number): string {
    const h = hex.replace('#', '');
    const full = h.length === 3 ? h.split('').map(c => c + c).join('') : h;
    const num = parseInt(full, 16);
    let r = (num >> 16) & 0xff, g = (num >> 8) & 0xff, b = num & 0xff;
    const target = percent < 0 ? 0 : 255;
    const p = Math.abs(percent) / 100;
    r = Math.round((target - r) * p) + r;
    g = Math.round((target - g) * p) + g;
    b = Math.round((target - b) * p) + b;
    return '#' + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1);
  }

  updateThemeOnHtmlEl(theme: string): void {
    document.querySelector('html')?.setAttribute('data-theme', theme);
  }

  updateButton(buttonEl: HTMLElement, isDark: boolean): void {
    const newCta = isDark ? 'dark' : 'light';
    buttonEl.setAttribute('aria-label', newCta);
    buttonEl.innerText = newCta;
  }

  calculateSettingAsThemeString(localStorageTheme: string | null): string {
    // Determine if a theme exists in localStorage, otherwise return 'light' by default
    return localStorageTheme === 'dark' ? 'dark' : 'light';
  }





  /** ✅ Set dynamic color and sync with Angular Material tokens */
  setColor(varName: string, color: string) {
    // set base variable
    document.documentElement.style.setProperty(varName, color);

    // ✅ sync Material color variables
    if (varName === '--primary') {
      document.documentElement.style.setProperty('--mdc-theme-primary', color);
      document.documentElement.style.setProperty('--mat-mdc-button-palette-primary50', color);
      document.documentElement.style.setProperty('--mat-mdc-raised-button-container-color', color);
    }
    if (varName === '--accent') {
      document.documentElement.style.setProperty('--mdc-theme-secondary', color);
    }

    // store in localStorage
    const current = this.getTheme();
    current[varName] = color;
    localStorage.setItem(this.storageKey, JSON.stringify(current));
  }

  /** ✅ Toggle light/dark mode */
  toggleDarkMode(enable: boolean) {
    if (enable) {
      document.body.classList.add('dark-theme');
    } else {
      document.body.classList.remove('dark-theme');
    }

    // Sync MDC surface + text colors dynamically
    const bg = getComputedStyle(document.body).getPropertyValue('--background').trim();
    const txt = getComputedStyle(document.body).getPropertyValue('--text').trim();
    document.documentElement.style.setProperty('--mdc-theme-surface', bg);
    document.documentElement.style.setProperty('--mdc-theme-on-surface', txt);

    const current = this.getTheme();
    current['darkMode'] = enable;
    localStorage.setItem(this.storageKey, JSON.stringify(current));
  }

  /** Load stored theme */
  loadTheme() {
    const saved = localStorage.getItem(this.storageKey);
    if (!saved) return;
    const theme = JSON.parse(saved);

    for (const key of Object.keys(theme)) {
      if (key.startsWith('--')) {
        document.documentElement.style.setProperty(key, theme[key]);
      }
    }

    if (theme['--primary'])
      document.documentElement.style.setProperty('--mdc-theme-primary', theme['--primary']);
    if (theme['--accent'])
      document.documentElement.style.setProperty('--mdc-theme-secondary', theme['--accent']);

    if (theme.darkMode) {
      document.body.classList.add('dark-theme');
    }
  }

  resetTheme() {
    localStorage.removeItem(this.storageKey);
    location.reload();
  }

  private getTheme() {
    return JSON.parse(localStorage.getItem(this.storageKey) || '{}');
  }
}


